import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import { PictureBehaviour } from "@jti/core-components/components/PictureBehaviour";
import type { BlogItemImage } from "@jti/ui/components/BlogListing/BlogItem";
import { useDeviceBreakpoint } from "@jti/ui/hooks/useDeviceBreakpoint";
import LoyaltyLogo from "@jti/ui/icons/loyalty_logo.svg";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { trackCtaClick } from "../../../../utils/trackCtaClick";
import type { RichTextComponent } from "../../ArticleRestrictedContent";

import styles from "./RestrictionMessage.module.scss";

export interface RestrictionMessageProps {
  primaryBtnUrl: string;
  secondaryBtnUrl: string;
  isLogged: boolean;
  primaryBtnUrlRestricted: string;
  restrictImage: BlogItemImage | null;
  dataTestId?: string;
  isInEditor?: boolean;
  richTextComponent: RichTextComponent;
}

export const RestrictionMessage: React.FC<RestrictionMessageProps> = ({
  primaryBtnUrl,
  secondaryBtnUrl,
  isLogged,
  primaryBtnUrlRestricted,
  restrictImage,
  dataTestId,
  isInEditor,
  richTextComponent: TextV2RichText,
}) => {
  const { __ } = useTranslations();
  const title = __(`loyalty.articleRestrictedContent.title${isLogged ? "Restricted" : "NotLogged"}`);
  const content = __(`loyalty.articleRestrictedContent.content${isLogged ? "Restricted" : "NotLogged"}`);
  const primaryBtnLabel = __(`loyalty.articleRestrictedContent.primaryBtn${isLogged ? "Restricted" : "NotLogged"}`);
  const secondaryBtnLabel = __(`loyalty.articleRestrictedContent.secondaryBtnNotLogged`);
  const urlPrimaryBtn = isLogged ? primaryBtnUrlRestricted : primaryBtnUrl;
  const isImagePresent = !isLogged && !!restrictImage?.largeReference;
  const { breakpoint } = useDeviceBreakpoint();
  const isMediumAndLarge = breakpoint !== "mobile";
  const buttonSize = isMediumAndLarge ? "lg" : "sm";

  const pictureSources = restrictImage
    ? [
        { srcSet: restrictImage.smallReference, media: "(max-width: 767px)" },
        { srcSet: restrictImage.mediumReference, media: "(min-width: 768px) and (max-width: 1279px)" },
      ]
    : [];

  const handleCtaButtonClick = (name: string) => () => {
    trackCtaClick(name, "RestrictionMessageCTA");
  };

  return (
    <div className={styles.restrictionMessage} data-testid={dataTestId}>
      {isImagePresent && (
        <div data-testid="restrictionMessageImagePlaceholder" className={styles.placeholderImageWrap}>
          <PictureBehaviour
            className={styles.placeholderPicture}
            sources={pictureSources}
            imageProps={{
              className: styles.placeholderImg,
              src: restrictImage.largeReference,
              alt: restrictImage.altText,
            }}
          />
        </div>
      )}
      <div className={styles.blockWrapper}>
        <div className={styles.contentTopBar}></div>
        <div className={classNames(styles.contentWrapper, isLogged && styles.centered)}>
          {isInEditor && (
            <span data-testid="restrictionMessagePreviewMessage" className={styles.previewMessage}>{`Preview for${
              isLogged ? "" : " not"
            } logged user. View as published for correct behavior`}</span>
          )}
          <LoyaltyLogo width="86px" height="52px" className={styles.logo} />
          <h2 className={styles.title}>{title}</h2>
          <div className={styles.text}>
            <TextV2RichText baseCssClass={styles.text} text={content} />
          </div>
          <div className={classNames(styles.btnPanel, isLogged && isMediumAndLarge && styles.centered)}>
            {!isLogged && (
              <Button
                data-testid="restrictedMessageSecondaryBtn"
                onClick={handleCtaButtonClick(secondaryBtnLabel)}
                size={buttonSize}
                variant="primary"
                href={secondaryBtnUrl}
              >
                {secondaryBtnLabel}
              </Button>
            )}
            <Button
              size={buttonSize}
              variant={isLogged ? "primary" : "secondary"}
              href={urlPrimaryBtn}
              onClick={handleCtaButtonClick(primaryBtnLabel)}
              data-testid="restrictedMessagePrimaryBtn"
            >
              {primaryBtnLabel}
            </Button>
          </div>
        </div>
      </div>
      {isImagePresent && (
        <div data-testid="restrictionMessageImagePlaceholder" className={styles.placeholderImageMobileWrap}>
          <PictureBehaviour
            className={styles.placeholderPicture}
            sources={pictureSources}
            imageProps={{
              className: styles.placeholderImg,
              src: restrictImage.largeReference,
              alt: restrictImage.altText,
            }}
          />
        </div>
      )}
    </div>
  );
};
